import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import axios, { AxiosError } from "axios";
import { format, parseISO } from "date-fns";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import * as yup from "yup";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { RisorseInfoCorrenti } from "../prismaTypes";
import { changeDatesToUTC } from "../utils/dateUtils";

const defaultValues = {
  id: 0,
  risorseId: 0,
  inizioValidita: "",
  fineValidita: "",
  durataSlot: "",
  numeroMinimoSlotPrenotabili: null,
  numeroMassimoSlotPrenotabili: null,
  massimoSlotPrenotabiliPerGiorno: "",
  oraInizioDisponibilita: "",
  oraFineDisponibilita: "",
  prenotazioneAnnullabileEntroMinuti: null,
  pagamentoAnticipato: false,
  pagamentoPosticipato: false,
  attivo: false,
  costoLuci: "",
  luciForfait: false,
  costoRiscaldamento: "",
  costoRiscaldamentoForfait: false,
};
type Inputs = {
  id?: number;
  risorseId: number;
  inizioValidita: Date | null | string;
  fineValidita: Date | null | string;
  durataSlot: number | string;
  numeroMinimoSlotPrenotabili: number | null;
  numeroMassimoSlotPrenotabili: number | null;
  massimoSlotPrenotabiliPerGiorno: number | string;
  oraInizioDisponibilita: string | null;
  oraFineDisponibilita: string | null;
  prenotazioneAnnullabileEntroMinuti: number | null;
  pagamentoAnticipato: boolean;
  pagamentoPosticipato: boolean;
  attivo: boolean;
  costoLuci: number | string;
  luciForfait: boolean;
  costoRiscaldamento: number | string;
  costoRiscaldamentoForfait: boolean;
};
const schema = yup
  .object({
    attivo: yup.boolean(),
    inizioValidita: yup
      .date()
      .typeError("Data non valida!")
      .label("Inizio Validità")
      .required()
      .nullable()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    fineValidita: yup
      .date()
      .typeError("Data non valida!")
      .label("Fine Validità")
      .required()
      .nullable()
      .default(undefined)
      .transform((curr, orig) => (orig === "" ? null : curr)),
    pagamentoPosticipato: yup.boolean(),
    pagamentoAnticipato: yup.boolean(),
    durataSlot: yup
      .number()
      .label("Durata Slot")
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(0)
      .required(),
    numeroMinimoSlotPrenotabili: yup
      .number()
      .label("Numero Minimo Slot Prenotabili")
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .min(0)
      .integer(),
    numeroMassimoSlotPrenotabili: yup
      .number()
      .label("Numero Massimo Slot Prenotabili")
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .integer(),
    massimoSlotPrenotabiliPerGiorno: yup
      .number()
      .label("Max slot per giorno")
      .transform((_, val) => (val === "" ? null : Number(val)))
      .min(1)
      .integer()
      .nullable(),
    oraInizioDisponibilita: yup
      .string()
      .label("Inizio Disponibilità")
      .transform((value) => (value === "" ? "" : value))
      .required(),
    oraFineDisponibilita: yup
      .string()
      .label("Fine Disponibilità")
      .transform((value) => (value === "" ? "" : value))
      .required(),
    costoLuci: yup
      .number()
      .label("Costo Luci")
      .transform((_, val) => (val === "" ? null : Number(val)))
      .min(0)
      .nullable(),
    costoRiscaldamento: yup
      .number()
      .label("Costo Riscaldamento")
      .transform((_, val) => (val === "" ? null : Number(val)))
      .min(0)
      .nullable(),
  })
  .required();

export function RisorsaInfo(props: { id?: number; risorseId?: number; handleClose: () => void }) {
  const { id, risorseId, handleClose } = props;

  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { refetch } = useQuery<{ risorsaInfo: RisorseInfoCorrenti }, AxiosError>(
    ["risorse-info", id],
    () => axios.get(API_URL + "risorse-info/getRisorsaInfo/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data: { risorsaInfo: RisorseInfoCorrenti }) => {
        const { risorsaInfo } = data;
        setLoaded(true);

        setValue("risorseId", risorsaInfo.risorseId);
        setValue("inizioValidita", risorsaInfo.inizioValidita);
        setValue("fineValidita", risorsaInfo.fineValidita);
        setValue("durataSlot", risorsaInfo.durataSlot);
        setValue("numeroMinimoSlotPrenotabili", risorsaInfo.numeroMinimoSlotPrenotabili);
        setValue("numeroMassimoSlotPrenotabili", risorsaInfo.numeroMassimoSlotPrenotabili);
        setValue(
          "massimoSlotPrenotabiliPerGiorno",
          risorsaInfo.massimoSlotPrenotabiliPerGiorno !== null
            ? risorsaInfo.massimoSlotPrenotabiliPerGiorno
            : "",
        );
        setValue(
          "prenotazioneAnnullabileEntroMinuti",
          risorsaInfo.prenotazioneAnnullabileEntroMinuti,
        );
        setValue("pagamentoAnticipato", risorsaInfo.pagamentoAnticipato);
        setValue("pagamentoPosticipato", risorsaInfo.pagamentoPosticipato);
        setValue("attivo", risorsaInfo.attivo);

        setValue(
          "oraInizioDisponibilita",
          risorsaInfo.oraInizioDisponibilita === null
            ? ""
            : format(parseISO(risorsaInfo.oraInizioDisponibilita.slice(0, -1)), "HH:mm"),
        );
        setValue(
          "oraFineDisponibilita",
          risorsaInfo.oraFineDisponibilita === null
            ? ""
            : format(parseISO(risorsaInfo.oraFineDisponibilita.slice(0, -1)), "HH:mm"),
        );

        setValue("costoLuci", risorsaInfo.costoLuci !== null ? risorsaInfo.costoLuci : "");
        setValue("luciForfait", risorsaInfo.luciForfait);
        setValue(
          "costoRiscaldamento",
          risorsaInfo.costoRiscaldamento !== null ? risorsaInfo.costoRiscaldamento : "",
        );
        setValue("costoRiscaldamentoForfait", risorsaInfo.costoRiscaldamentoForfait);
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const { mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "risorse-info/create", data);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Dettaglio Creato", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.patch(API_URL + "risorse-info/" + id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Risorsa Aggiornata", { variant: "success" });
        handleClose();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    },
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: Inputs) => {
    changeDatesToUTC(data);
    if (id !== undefined && id > 0) mutateUpdate({ ...data, id });
    else if (risorseId) mutate({ ...data, risorseId });
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined || id < 1) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  if (!loaded) return <></>;

  return (
    <Box className={animations ? "" : "noAnimation"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container columnSpacing={1}>
            <Grid item xs={12}>
              <Controller
                name="attivo"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("attivo", v.target.checked)}
                      />
                    }
                    label="Attiva"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="inizioValidita"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    mask={"__/__/____"}
                    label="Inizio Validità"
                    onChange={field.onChange}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        sx={{ mr: 2 }}
                        variant="standard"
                        helperText={errors.inizioValidita?.message ?? " "}
                        {...params}
                        error={Boolean(errors.inizioValidita)}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="fineValidita"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    mask={"__/__/____"}
                    label="Fine Validita"
                    onChange={field.onChange}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        sx={{ mr: 2 }}
                        variant="standard"
                        helperText={errors.fineValidita?.message ?? " "}
                        {...params}
                        error={Boolean(errors.fineValidita)}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Controller
                name="durataSlot"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Durata Slots"
                    type="number"
                    InputProps={{ inputProps: { min: "0", step: "1" } }}
                    error={Boolean(errors.durataSlot)}
                    helperText={errors.durataSlot?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Controller
                name="numeroMinimoSlotPrenotabili"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Num Minimo Slot"
                    type="number"
                    InputProps={{ inputProps: { min: "0", step: "1" } }}
                    error={Boolean(errors.numeroMinimoSlotPrenotabili)}
                    helperText={errors.numeroMinimoSlotPrenotabili?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Controller
                name="numeroMassimoSlotPrenotabili"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Num Massimo Slot"
                    type="number"
                    InputProps={{ inputProps: { min: "0", step: "1" } }}
                    error={Boolean(errors.numeroMassimoSlotPrenotabili)}
                    helperText={errors.numeroMassimoSlotPrenotabili?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Controller
                name="massimoSlotPrenotabiliPerGiorno"
                control={control}
                defaultValue={""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Massimo nr. di slot prenotabile per giorno"
                    type="number"
                    InputProps={{ inputProps: { min: "1", step: "1" } }}
                    error={Boolean(errors.massimoSlotPrenotabiliPerGiorno)}
                    helperText={
                      errors.massimoSlotPrenotabiliPerGiorno?.message ??
                      "I soci non potranno prenotare più slot per socio/giorno"
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="oraInizioDisponibilita"
                control={control}
                defaultValue={""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: 150, mt: 1, mr: 2 }}
                    label="Inizio Disponibilità"
                    type="time"
                    error={Boolean(errors.oraInizioDisponibilita)}
                    helperText={errors.oraInizioDisponibilita?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="oraFineDisponibilita"
                control={control}
                defaultValue={""}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: 150, mt: 1, mr: 2 }}
                    label="Fine Disponibilità"
                    type="time"
                    error={Boolean(errors.oraFineDisponibilita)}
                    helperText={errors.oraFineDisponibilita?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="prenotazioneAnnullabileEntroMinuti"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    sx={{ mt: 1, mr: 2 }}
                    variant="standard"
                    label="Annullabilità (minuti)"
                    type="number"
                    InputProps={{ inputProps: { min: "0", step: "1" } }}
                    error={Boolean(errors.prenotazioneAnnullabileEntroMinuti)}
                    helperText={errors.prenotazioneAnnullabileEntroMinuti?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="pagamentoAnticipato"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("pagamentoAnticipato", v.target.checked)}
                      />
                    }
                    label="Pagamento a prenotazione"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name="pagamentoPosticipato"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("pagamentoPosticipato", v.target.checked)}
                      />
                    }
                    label="Pagamento a scadenza"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <Controller
                name="costoLuci"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    InputProps={{ inputProps: { min: "0", step: "0.01" } }}
                    variant="standard"
                    label="Costo Luci"
                    type="number"
                    error={Boolean(errors.costoLuci)}
                    helperText={errors.costoLuci?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={7}>
              <Controller
                name="luciForfait"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("luciForfait", v.target.checked)}
                      />
                    }
                    label="Luci forfait"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <Controller
                name="costoRiscaldamento"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Costo Riscaldamento"
                    InputProps={{ inputProps: { min: "0", step: "0.01" } }}
                    type="number"
                    error={Boolean(errors.costoRiscaldamento)}
                    helperText={errors.costoRiscaldamento?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={7}>
              <Controller
                name="costoRiscaldamentoForfait"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("costoRiscaldamentoForfait", v.target.checked)}
                      />
                    }
                    label="Riscaldamento forfait"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          {id === undefined && <Button type="submit">Crea Dettaglio funzionale</Button>}
          {id && <Button type="submit">Modifica Dettaglio funzionale</Button>}
        </DialogActions>
      </form>
    </Box>
  );
}
